/**
 * LEAN THEME - Cart Drawer JavaScript
 * Complete Ajax Cart with Accessibility Support.
 *
 * Features:
 * - Ajax Add/Update/Remove
 * - Section Rendering API
 * - Focus Trap for A11y
 * - Escape to close
 * - Cart count badge animation
 * - Undo item removal
 * - Discount code support
 * - Cart type (drawer / page redirect)
 */

'use strict';

const LeanCart = (() => {
  // State
  let isOpen = false;
  let isUpdating = false;
  let triggerElement = null;
  let focusableElements = [];
  let firstFocusable = null;
  let lastFocusable = null;

  // Undo state
  let undoData = null;
  let undoTimeout = null;
  const UNDO_DELAY = 5000;

  // DOM Cache
  const elements = {};

  // Selectors
  const SELECTORS = {
    drawer: '[data-cart-drawer]',
    backdrop: '.cart-drawer__backdrop',
    panel: '.cart-drawer__panel',
    close: '[data-cart-drawer-close]',
    items: '[data-cart-items]',
    item: '[data-cart-item]',
    footer: '[data-cart-footer]',
    subtotal: '[data-cart-subtotal]',
    count: '[data-cart-count]',
    countBadge: '[data-cart-count-badge]',
    loading: '[data-cart-loading]',
    shippingMeter: '[data-shipping-meter]',
    qtyChange: '[data-qty-change]',
    qtyInput: '.cart-item__qty-input',
    removeItem: '[data-remove-item]',
    cartNote: '[data-cart-note]',
    cartIcon: '[data-cart-icon]',
    undoToast: '[data-undo-toast]',
    undoBtn: '[data-undo-btn]',
    undoText: '[data-undo-text]',
    discountForm: '[data-discount-form]',
    discountInput: '[data-discount-input]',
    discountApply: '[data-discount-apply]',
    discountMessage: '[data-discount-message]',
    emptyRecs: '[data-empty-recommendations]'
  };

  /**
   * Cache DOM elements
   */
  const cacheElements = () => {
    elements.drawer = document.querySelector(SELECTORS.drawer);
    elements.backdrop = document.querySelector(SELECTORS.backdrop);
    elements.panel = document.querySelector(SELECTORS.panel);
    elements.items = document.querySelector(SELECTORS.items);
    elements.footer = document.querySelector(SELECTORS.footer);
    elements.loading = document.querySelector(SELECTORS.loading);
    elements.shippingMeter = document.querySelector(SELECTORS.shippingMeter);
  };

  /**
   * Update focusable elements for focus trap
   */
  const updateFocusableElements = () => {
    if (!elements.panel) return;

    const focusableSelectors = [
      'button:not([disabled])',
      'a[href]',
      'input:not([disabled])',
      'textarea:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    focusableElements = [...elements.panel.querySelectorAll(focusableSelectors)];
    firstFocusable = focusableElements[0];
    lastFocusable = focusableElements[focusableElements.length - 1];
  };

  /**
   * Focus trap handler
   * @param {KeyboardEvent} e
   */
  const handleFocusTrap = (e) => {
    if (e.key !== 'Tab' || !isOpen) return;

    updateFocusableElements();

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable?.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable?.focus();
      }
    }
  };

  /**
   * Open cart drawer
   * @param {Element} [trigger] - Element that triggered the open
   */
  const open = (trigger = null) => {
    if (!elements.drawer || isOpen) return;

    triggerElement = trigger || document.activeElement;
    isOpen = true;

    // Update ARIA
    elements.drawer.setAttribute('aria-hidden', 'false');
    elements.drawer.classList.add('is-open');
    document.body.classList.add('no-scroll');

    // Refresh cart and focus
    refreshCart().then(() => {
      updateFocusableElements();
      elements.panel?.focus();
    });

    // Dispatch event
    document.dispatchEvent(new CustomEvent('cart:opened'));
  };

  /**
   * Close cart drawer
   */
  const close = () => {
    if (!elements.drawer || !isOpen) return;

    isOpen = false;

    // Update ARIA
    elements.drawer.setAttribute('aria-hidden', 'true');
    elements.drawer.classList.remove('is-open');
    document.body.classList.remove('no-scroll');

    // Return focus to trigger
    if (triggerElement && triggerElement.focus) {
      triggerElement.focus();
    }

    triggerElement = null;

    // Dispatch event
    document.dispatchEvent(new CustomEvent('cart:closed'));
  };

  /**
   * Toggle cart drawer
   */
  const toggle = () => {
    isOpen ? close() : open();
  };

  /**
   * Show loading state
   */
  const showLoading = () => {
    elements.loading?.removeAttribute('hidden');
  };

  /**
   * Hide loading state
   */
  const hideLoading = () => {
    elements.loading?.setAttribute('hidden', '');
  };

  /**
   * Fetch cart data
   * @returns {Promise<Object|null>}
   */
  const fetchCart = async () => {
    try {
      const response = await fetch('/cart.js', {
        headers: { 'Content-Type': 'application/json' }
      });
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      return null;
    }
  };

  /**
   * Refresh cart drawer using Section Rendering API
   */
  const refreshCart = async () => {
    try {
      const response = await fetch('/?section_id=cart-drawer');
      const html = await response.text();

      // Parse HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newDrawer = doc.querySelector(SELECTORS.drawer);

      if (newDrawer && elements.drawer) {
        // Preserve open state class
        const wasOpen = elements.drawer.classList.contains('is-open');

        // Replace inner content but keep container
        const newPanel = newDrawer.querySelector(SELECTORS.panel);
        const currentPanel = elements.drawer.querySelector(SELECTORS.panel);

        if (newPanel && currentPanel) {
          currentPanel.innerHTML = newPanel.innerHTML;

          // Inline <script> tags don't execute when injected via innerHTML.
          // Re-execute scripts marked with data-cart-reinit (recommendations, etc.)
          currentPanel.querySelectorAll('script[data-cart-reinit]').forEach(oldScript => {
            const newScript = document.createElement('script');
            newScript.textContent = oldScript.textContent;
            oldScript.parentNode.replaceChild(newScript, oldScript);
          });
        }

        // Re-cache elements
        elements.items = elements.drawer.querySelector(SELECTORS.items);
        elements.footer = elements.drawer.querySelector(SELECTORS.footer);
        elements.loading = elements.drawer.querySelector(SELECTORS.loading);
        elements.shippingMeter = elements.drawer.querySelector(SELECTORS.shippingMeter);

        // Maintain open state
        if (wasOpen) {
          elements.drawer.classList.add('is-open');
        }
      }

      // Re-init all cart drawer sub-components after HTML refresh.
      // The data-cart-reinit script re-execution above handles the primary
      // init, but these explicit calls act as a fallback (e.g. if CSP blocks
      // inline script re-execution or a script error prevents init).
      try {
        if (window.LeanCountdown) window.LeanCountdown.init();

        // Reset loaded flags so .init() actually runs (the new DOM elements
        // from innerHTML won't carry the old flags, but if the data-cart-reinit
        // script already ran and set them, we need to clear for the fallback).
        var upsellContainer = document.querySelector('[data-cart-upsell]');
        if (upsellContainer) upsellContainer._upsellLoaded = false;
        if (window.LeanCartUpsell) window.LeanCartUpsell.init();

        var crossSellContainer = document.querySelector('[data-cross-sell-cart]');
        if (crossSellContainer) crossSellContainer._crossSellLoaded = false;
        if (window.LeanCrossSell) window.LeanCrossSell.init();

        // Re-init cart notes (character counters on new DOM elements)
        if (window.LeanCartNotes && typeof window.LeanCartNotes.init === 'function') {
          window.LeanCartNotes.init();
        }
      } catch (reinitError) {
        console.warn('[LeanCart] Component re-init error:', reinitError);
      }

      // Update badges
      await updateCartBadges();

    } catch (error) {
      console.error('Failed to refresh cart:', error);
    }
  };

  /**
   * Format money
   * @param {number} cents
   * @returns {string}
   */
  // eslint-disable-next-line no-unused-vars -- available for external use
  const formatMoney = (cents) => {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    if (window.LeanTheme?.formatMoney) {
      return window.LeanTheme.formatMoney(cents);
    }
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  };

  /**
   * Update all cart count badges
   */
  const updateCartBadges = async () => {
    const cart = await fetchCart();
    if (!cart) return;

    const count = cart.item_count;

    // Update drawer count
    document.querySelectorAll(SELECTORS.count).forEach(el => {
      el.textContent = `(${count})`;
    });

    // Update icon badges
    document.querySelectorAll(SELECTORS.countBadge).forEach(badge => {
      badge.textContent = count;

      if (count > 0) {
        badge.classList.remove('cart-icon__badge--hidden');
        // Bump animation
        badge.classList.remove('is-bumping');
        void badge.offsetWidth;
        badge.classList.add('is-bumping');
      } else {
        badge.classList.add('cart-icon__badge--hidden');
      }
    });

    // Update shipping meter
    updateShippingMeter(cart.total_price);

    return cart;
  };

  /**
   * Notify shipping meter of updated cart total
   * @param {number} totalPrice - Cart total in cents
   */
  const updateShippingMeter = (totalPrice) => {
    document.dispatchEvent(new CustomEvent('shipping-meter:update', {
      detail: { totalPrice }
    }));
  };

  /**
   * Update cart item quantity
   * @param {string} key - Line item key
   * @param {number} quantity
   */
  const updateItem = async (key, quantity) => {
    if (isUpdating) return;
    isUpdating = true;

    // Show loading on specific item
    const item = document.querySelector(`${SELECTORS.item}[data-line-key="${key}"]`);
    item?.classList.add('is-updating');

    showLoading();

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });

      if (!response.ok) throw new Error('Update failed');

      await refreshCart();

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'leancart' } }));
    } catch (error) {
      console.error('Cart update error:', error);
      item?.classList.remove('is-updating');
    } finally {
      isUpdating = false;
      hideLoading();
    }
  };

  /**
   * Remove item from cart (with undo support)
   * @param {string} key
   */
  const removeItem = async (key) => {
    // Capture item data before removal for undo
    const cart = await fetchCart();
    if (cart) {
      const lineItem = cart.items.find(item => item.key === key);
      if (lineItem) {
        undoData = {
          id: lineItem.variant_id,
          quantity: lineItem.quantity,
          properties: lineItem.properties || {}
        };
      }
    }

    await updateItem(key, 0);
    showUndoToast();
  };

  /**
   * Show undo toast after item removal
   */
  const showUndoToast = () => {
    const toast = document.querySelector(SELECTORS.undoToast);
    if (!toast || !undoData) return;

    clearTimeout(undoTimeout);
    toast.removeAttribute('hidden');

    undoTimeout = setTimeout(() => {
      hideUndoToast();
    }, UNDO_DELAY);
  };

  /**
   * Hide undo toast
   */
  const hideUndoToast = () => {
    const toast = document.querySelector(SELECTORS.undoToast);
    if (toast) toast.setAttribute('hidden', '');
    undoData = null;
    clearTimeout(undoTimeout);
  };

  /**
   * Undo last removal – re-add item to cart
   */
  const undoRemove = async () => {
    if (!undoData) return;

    const data = { ...undoData };
    hideUndoToast();

    try {
      await addItem({ id: data.id, quantity: data.quantity, properties: data.properties }, false);
    } catch (err) {
      console.error('Undo failed:', err);
    }
  };

  /**
   * Add item to cart
   * @param {FormData|Object} data
   * @param {boolean} openDrawer
   */
  const addItem = async (data, openDrawer = true) => {
    showLoading();

    try {
      const body = data instanceof FormData ? data : JSON.stringify(data);
      const headers = data instanceof FormData ? {} : { 'Content-Type': 'application/json' };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers,
        body
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const msg = errorData.description || errorData.message || 'Add to cart failed';
        console.error('Shopify /cart/add.js error:', response.status, errorData);
        throw new Error(msg);
      }

      const item = await response.json();

      await refreshCart();

      if (openDrawer) {
        open();
      }

      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: item }));

      return item;
    } catch (error) {
      console.error('Add to cart error:', error);
      throw error;
    } finally {
      hideLoading();
    }
  };

  /**
   * Update cart note
   * @param {string} note
   */
  const updateNote = async (note) => {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note })
      });
    } catch (error) {
      console.error('Failed to update note:', error);
    }
  };

  /**
   * Apply discount code
   * Shopify doesn't have a public AJAX discount API, so we use
   * the /discount/{code} route and then refresh the cart.
   * @param {string} code
   */
  const applyDiscount = async (code) => {
    if (!code || !code.trim()) return;

    const msgEl = document.querySelector(SELECTORS.discountMessage);
    const inputEl = document.querySelector(SELECTORS.discountInput);
    const applyBtn = document.querySelector(SELECTORS.discountApply);

    if (applyBtn) applyBtn.disabled = true;

    try {
      await fetch(`/discount/${encodeURIComponent(code.trim())}`, {
        method: 'GET',
        redirect: 'follow'
      });

      if (msgEl) {
        msgEl.removeAttribute('hidden');
        msgEl.className = 'discount__message discount__message--success';
        msgEl.textContent = inputEl?.dataset.msgSuccess || 'Rabattcode angewendet!';
      }

      // Refresh cart to show updated prices
      await refreshCart();

    } catch (error) {
      console.error('Discount apply error:', error);
      if (msgEl) {
        msgEl.removeAttribute('hidden');
        msgEl.className = 'discount__message discount__message--error';
        msgEl.textContent = inputEl?.dataset.msgError || 'Ungültiger Rabattcode.';
      }
    } finally {
      if (applyBtn) applyBtn.disabled = false;
    }
  };

  /**
   * Handle discount apply button click
   * @param {Event} e
   */
  const handleDiscountApply = (e) => {
    const btn = e.target.closest(SELECTORS.discountApply);
    if (!btn) return;

    const input = document.querySelector(SELECTORS.discountInput);
    if (input?.value) {
      applyDiscount(input.value);
    }
  };

  /**
   * Handle discount input Enter key
   * @param {KeyboardEvent} e
   */
  const handleDiscountKeydown = (e) => {
    if (e.key === 'Enter' && e.target.matches(SELECTORS.discountInput)) {
      e.preventDefault();
      if (e.target.value) {
        applyDiscount(e.target.value);
      }
    }
  };

  /**
   * Load product recommendations for empty cart
   */
  const loadEmptyRecommendations = async () => {
    const container = document.querySelector(SELECTORS.emptyRecs);
    if (!container) return;

    try {
      // Use a collection or featured products as source
      const response = await fetch('/collections/all.json?limit=4');
      if (!response.ok) return;
      const data = await response.json();
      const products = data.collection?.products || data.products || [];

      if (products.length === 0) return;

      let html = '<p class="empty-recs__heading">Beliebte Produkte</p>';
      html += '<div class="empty-recs__grid">';

      products.slice(0, 4).forEach(product => {
        const img = product.images?.[0]?.src || product.image?.src || '';
        const price = product.variants?.[0]?.price || product.price || '0.00';
        html += `
          <a href="/products/${product.handle}" class="empty-rec__card">
            ${img ? `<img src="${img}&width=200" alt="${product.title}" class="empty-rec__image" width="100" height="100" loading="lazy">` : ''}
            <p class="empty-rec__title">${product.title}</p>
            <p class="empty-rec__price">${price} €</p>
          </a>
        `;
      });

      html += '</div>';
      container.innerHTML = html;
    } catch (err) {
      // Silently fail – recommendations are optional
    }
  };

  /**
   * Handle add to cart form submission
   * @param {Event} e
   */
  const handleAddToCart = async (e) => {
    const form = e.target;
    const action = form.getAttribute('action');

    if (!action || !action.includes('/cart/add')) return;

    const variantId = form.querySelector('[name="id"]')?.value;

    if (!variantId) {
      console.error('[LeanCart] No variant ID found — letting form submit natively');
      return; // Don't prevent default, let native form submit
    }

    e.preventDefault();

    const btn = form.querySelector('[type="submit"]');
    if (!btn) return;

    const originalHTML = btn.innerHTML;

    btn.disabled = true;
    btn.textContent = 'Wird hinzugefügt...';

    try {
      const formData = new FormData(form);

      const isPageMode = getCartType() === 'page';
      const autoOpen = !isPageMode && elements.drawer?.dataset.autoOpen !== 'false';
      await addItem(formData, autoOpen);

      btn.textContent = 'Hinzugefügt!';
      btn.classList.add('is-success');

      setTimeout(() => {
        btn.disabled = false;
        btn.innerHTML = originalHTML;
        btn.classList.remove('is-success');
      }, 1500);

    } catch (error) {
      console.error('[LeanCart] AJAX failed:', error.message, '— falling back to native submit');
      btn.disabled = false;
      btn.innerHTML = originalHTML;
      form.submit();
    }
  };

  /**
   * Handle quantity button clicks
   * @param {Event} e
   */
  const handleQuantityClick = (e) => {
    const btn = e.target.closest(SELECTORS.qtyChange);
    if (!btn) return;

    const key = btn.dataset.lineKey;
    const change = parseInt(btn.dataset.qtyChange) || 0;
    const item = btn.closest(SELECTORS.item);
    const input = item?.querySelector(SELECTORS.qtyInput);
    const currentQty = parseInt(input?.value) || 1;
    const newQty = Math.max(0, currentQty + change);

    updateItem(key, newQty);
  };

  /**
   * Handle quantity input change
   * @param {Event} e
   */
  const handleQuantityInput = (e) => {
    if (!e.target.matches(SELECTORS.qtyInput)) return;

    const key = e.target.dataset.lineKey;
    const newQty = parseInt(e.target.value) || 0;

    updateItem(key, newQty);
  };

  /**
   * Handle remove button click
   * @param {Event} e
   */
  const handleRemoveClick = (e) => {
    const btn = e.target.closest(SELECTORS.removeItem);
    if (!btn) return;

    const key = btn.dataset.lineKey;
    removeItem(key);
  };

  /**
   * Handle cart icon click
   * @param {Event} e
   */
  const handleCartIconClick = (e) => {
    const icon = e.target.closest(SELECTORS.cartIcon);
    if (!icon) return;

    e.preventDefault();

    // If cart type is 'page', redirect instead of opening drawer
    if (getCartType() === 'page') {
      window.location.href = icon.getAttribute('href') || '/cart';
      return;
    }

    open(icon);
  };

  /**
   * Handle close button/backdrop click
   * @param {Event} e
   */
  const handleCloseClick = (e) => {
    if (e.target.closest(SELECTORS.close)) {
      close();
    }
  };

  /**
   * Handle keyboard events
   * @param {KeyboardEvent} e
   */
  const handleKeydown = (e) => {
    if (e.key === 'Escape' && isOpen) {
      close();
    }

    handleFocusTrap(e);
  };

  /**
   * Handle cart note change (debounced)
   * @param {Event} e
   */
  let noteTimeout;
  const handleNoteChange = (e) => {
    if (!e.target.matches(SELECTORS.cartNote)) return;

    clearTimeout(noteTimeout);
    noteTimeout = setTimeout(() => {
      updateNote(e.target.value);
    }, 500);
  };

  /**
   * Handle undo button click
   * @param {Event} e
   */
  const handleUndoClick = (e) => {
    if (e.target.closest(SELECTORS.undoBtn)) {
      undoRemove();
    }
  };

  /**
   * Get cart type from drawer data attribute
   * @returns {string} 'drawer' or 'page'
   */
  const getCartType = () => {
    return elements.drawer?.dataset.cartType || 'drawer';
  };

  /**
   * Initialize event listeners
   */
  const bindEvents = () => {
    // Form submissions
    document.addEventListener('submit', handleAddToCart);

    // Click events (delegated)
    document.addEventListener('click', (e) => {
      handleCartIconClick(e);
      handleCloseClick(e);
      handleQuantityClick(e);
      handleRemoveClick(e);
      handleUndoClick(e);
      handleDiscountApply(e);
    });

    // Input changes
    document.addEventListener('change', (e) => {
      handleQuantityInput(e);
      handleNoteChange(e);
    });

    // Keyboard
    document.addEventListener('keydown', (e) => {
      handleKeydown(e);
      handleDiscountKeydown(e);
    });

    // Custom events
    document.addEventListener('cart:open', () => open());
    document.addEventListener('cart:close', () => close());

    // External cart:updated (from FBT, Cross-Sell, etc.)
    // Skip internal dispatches (from updateItem) to avoid double-refresh.
    document.addEventListener('cart:updated', (e) => {
      if (e.detail?.source === 'leancart') return;
      if (isOpen) {
        refreshCart();
      } else {
        open();
      }
    });
  };

  /**
   * Initialize cart module
   */
  const init = () => {
    cacheElements();
    bindEvents();

    // Initial badge update
    updateCartBadges();

    // Load empty cart recommendations if cart is empty
    loadEmptyRecommendations();

    // Dispatch ready event
    document.dispatchEvent(new CustomEvent('cart:ready'));
  };

  // Public API
  return {
    init,
    open,
    close,
    toggle,
    refreshCart,
    addItem,
    updateItem,
    removeItem,
    updateNote,
    updateCartBadges,
    applyDiscount,
    isOpen: () => isOpen
  };
})();

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', LeanCart.init);
} else {
  LeanCart.init();
}

// Expose for external access
window.LeanCart = LeanCart;
window.openCartDrawer = LeanCart.open;
window.closeCartDrawer = LeanCart.close;
window.refreshCartDrawer = LeanCart.refreshCart;
window.updateCartBadge = LeanCart.updateCartBadges;
